import { DecimalFormatOptions, IntegerFormatOptions } from 'interfaces-lib';
import { ConditionalFieldsComponent } from '../../../components/field-selection/conditional-fields/conditional-fields.component';
import { Condition } from '../../condition';
import { IAttribute } from '../../dom';
import {
  DateFormatOptions,
  ImageFieldOptions,
  ImageFieldType,
  StringFormatOptions,
} from '../../fieldconfig';
import { FieldContext } from '../../fieldcontext';
import { FieldType } from '../../fieldtype';

export abstract class ThTextAttribute implements IAttribute {
  public static QUALIFIED_NAME = 'th:text';
  protected srcFieldPath: string;
  protected _defaultEmptyValue: any;

  protected constructor(fieldPath: string, defaultEmptyValue?: string) {
    this.srcFieldPath = fieldPath;
    this._defaultEmptyValue = defaultEmptyValue;
  }

  get qualifiedName(): string {
    return ThTextAttribute.QUALIFIED_NAME;
  }

  abstract get value(): string;

  set defaultEmptyValue(value: string) {
    this._defaultEmptyValue = value;
  }

  toString(): string {
    return `${ThTextAttribute.QUALIFIED_NAME}="${this.value}"`;
  }
}

export class StringThTextAttribute extends ThTextAttribute {
  private _stringFormat: StringFormatOptions;

  constructor(fieldPath: string, stringFormat: StringFormatOptions, defaultEmptyValue?: string) {
    super(fieldPath, defaultEmptyValue);
    this._stringFormat = stringFormat;
  }
  override get value(): string {
    if (this._stringFormat.renderHtml) {
      return this.htmlString();
    }

    return this.plainTextString();
  }

  private plainTextString(): string {
    const path =
      `#strings.formatString(${this.srcFieldPath},${this._stringFormat.removeHtmlTags},` +
      `'${this._stringFormat.substringType}',${this._stringFormat.substringFromStart},` +
      `${this._stringFormat.substringFromEnd},${this._stringFormat.substringRangeStart},` +
      `${this._stringFormat.substringRangeEnd},'${this._stringFormat.capitalisation}')`;
    let value = `\${${path}}`;

    if (this._defaultEmptyValue) {
      // this will skip empty string, but also NUMBER zero, but shouldn't we always be getting a STRING?
      value = `\${#strings.defaultString(${path}, '${this._defaultEmptyValue}')}`;
    }

    return value;
  }

  private htmlString(): string {
    const defaultEmptyValue = this._defaultEmptyValue ?? '';
    return `\${#strings.html(${this.srcFieldPath}, '${defaultEmptyValue}')}`;
  }
}

export class DisplayValueThTextAttribute extends StringThTextAttribute {
  constructor(fieldPath: string, stringFormat: StringFormatOptions, defaultEmptyValue?: any) {
    super(fieldPath, stringFormat, defaultEmptyValue);
    this.srcFieldPath = this.buildSrcPath(fieldPath);
  }

  private buildSrcPath(srcFieldPath: string): string {
    const pathParts = srcFieldPath.split('.');
    const tailless = pathParts.slice(0, -1).join('.');
    const tail = pathParts[pathParts.length - 1];
    const finalSrcFieldPath = `${tailless}.additionalProperties.${tail}_displayValues.get(${this.srcFieldPath})`;
    return finalSrcFieldPath;
  }
}

export class DateThTextAttribute extends ThTextAttribute {
  private _dateFormatOptions: DateFormatOptions;

  constructor(fieldPath: string, dateFormatOptions: DateFormatOptions, defaultEmptyValue?: string) {
    super(fieldPath, defaultEmptyValue);
    this._dateFormatOptions = dateFormatOptions;
  }

  set dateFormatOptions(format: DateFormatOptions) {
    this._dateFormatOptions = format;
  }

  override get value(): string {
    let value = `\${#temporals.format(${this.srcFieldPath}, '${this._dateFormatOptions.dateFormat}', '${this._dateFormatOptions.language}')}`;

    if (this._defaultEmptyValue) {
      value += ` ?: '${this._defaultEmptyValue}'`;
    }

    return value;
  }
}

export class IntegerThTextAttribute extends ThTextAttribute {
  private _integerFormat: IntegerFormatOptions;

  constructor(fieldPath: string, integerFormat: IntegerFormatOptions, defaultEmptyValue?: string) {
    super(fieldPath, defaultEmptyValue);
    this._integerFormat = integerFormat;
  }

  set integerFormat(format: IntegerFormatOptions) {
    this._integerFormat = format;
  }

  override get value(): string {
    let value = `\${#numbers.formatInteger(${this.srcFieldPath}, ${this._integerFormat.minimumIntegerDigits}, '${this._integerFormat.thousandsSeparator}')}`;

    if (this.hasDefaultEmptyValue()) {
      value += ` ?: '${this._defaultEmptyValue}'`;
    }

    return value;
  }

  private hasDefaultEmptyValue(): boolean {
    return this._defaultEmptyValue !== undefined && this._defaultEmptyValue !== null;
  }
}

export class DecimalThTextAttribute extends ThTextAttribute {
  private _decimalFormat: DecimalFormatOptions;

  constructor(fieldPath: string, decimalFormat: DecimalFormatOptions, defaultEmptyValue?: string) {
    super(fieldPath, defaultEmptyValue);
    this._decimalFormat = decimalFormat;
  }

  set decimalFormat(format: DecimalFormatOptions) {
    this._decimalFormat = format;
  }

  override get value(): string {
    const minimumIntegerDigits = this._decimalFormat.minimumIntegerDigits;
    const thousandsSeparator = this._decimalFormat.thousandsSeparator;
    const exactDecimalDigits = this._decimalFormat.exactDecimalDigits;
    const decimalSeparator = this._decimalFormat.decimalSeparator;

    // Both names are methods on the back end's NumbersWrapper
    const formatMethod = this._decimalFormat.isPercentage ? 'formatPercentageDecimal' : 'formatDecimal';
    let value = `\${#numbers.${formatMethod}(${this.srcFieldPath}, ${minimumIntegerDigits}, '${thousandsSeparator}', ${exactDecimalDigits}, '${decimalSeparator}')}`;

    if (this.hasDefaultEmptyValue()) {
      value += ` ?: '${this._defaultEmptyValue}'`;
    }

    return value;
  }

  private hasDefaultEmptyValue(): boolean {
    return this._defaultEmptyValue !== undefined && this._defaultEmptyValue !== null;
  }
}

export class ImageThTextAttribute extends ThTextAttribute {
  private _namespace: string;
  private _imageFieldConfig: ImageFieldOptions;

  constructor(namespace: string, fieldPath: string, imageFieldConfig: ImageFieldOptions) {
    super(fieldPath);
    this._namespace = namespace;
    this._imageFieldConfig = imageFieldConfig;
  }

  override get value(): string {
    let value = '';

    if (this._imageFieldConfig.type === ImageFieldType.URL) {
      value = `\${#files.url('${this._imageFieldConfig.value}')}`;
    } else if (this._imageFieldConfig.type === ImageFieldType.ENTITY_FIELD) {
      value = `\${#files.url(${this._namespace}.${this._imageFieldConfig.value})}`;
    } else if (this._imageFieldConfig.type === ImageFieldType.SCRIPT_FIELD) {
      value = `\${#files.url(${this._imageFieldConfig.value})}`;
    } else if (this._imageFieldConfig.type === ImageFieldType.FILE_TYPE) {
      value = `\${#files.content(${this.srcFieldPath})}`;
    }

    console.debug('ImageThTextAttribute', this.srcFieldPath, value);

    return value;
  }
}

export class ConditionalThTextAttribute extends ThTextAttribute {
  private readonly _namespace: string;
  private readonly _conditions: Condition[];

  constructor(namespace: string, dataSourcePath: string, conditions: Condition[]) {
    super(dataSourcePath);
    this._namespace = namespace;
    this._conditions = conditions;

    if (!this._conditions || this._conditions.length === 0) {
      throw new Error('Condition is not initialized.');
    }
  }

  override get value(): string {
    const fullyQualifiedFieldPath = `${this._namespace}.${this._conditions[0].fieldPath}`;
    const operator = this._conditions[0].operator;
    const conditionValue = this._conditions[0].value;

    if (!operator) {
      throw new Error('Missing operator on condition');
    }

    console.log(`fieldPath: ${fullyQualifiedFieldPath}\noperator: ${operator}\nconditionValue: ${conditionValue}`);

    const formattedPath = `#strings.formatString(${fullyQualifiedFieldPath},false,\'NONE\',-1,-1,-1,-1,\'NONE\')`;

    if (operator === ConditionalFieldsComponent.EQUALS) {
      return `\${#strings.equals(${formattedPath}, '${conditionValue}')}`;
    }

    if (operator === ConditionalFieldsComponent.POPULATED) {
      return `\${!#strings.isEmpty(${formattedPath})}`;
    }

    if (operator === ConditionalFieldsComponent.NOT_POPULATED) {
      return `\${#strings.isEmpty(${formattedPath})}`;
    }

    return '';
  }
}

export class ThTextAttributeFactory {
  // the system-default field options should guarantee that formatting options are populated

  static make(dataSrcPath: string, fieldContext: FieldContext): IAttribute {
    if (fieldContext.useDisplayValue) {
      return new DisplayValueThTextAttribute(dataSrcPath, fieldContext.stringFormat!, fieldContext.defaultEmptyValue);
    }

    if (fieldContext.isImageField) {
      return new ImageThTextAttribute(fieldContext.namespace, dataSrcPath, fieldContext.imageFieldConfig!);
    }

    if (fieldContext.isConditionalField) {
      const escapedConditions = ThTextAttributeFactory.escapeConditions(fieldContext.conditions!);
      return new ConditionalThTextAttribute(fieldContext.namespace, dataSrcPath, escapedConditions);
    }

    if (fieldContext.isAiAssistField) {
      return new StringThTextAttribute(dataSrcPath, fieldContext.stringFormat!, fieldContext.defaultEmptyValue);
    }

    switch (fieldContext.field.type) {
      case FieldType.STRING:
      case FieldType.BOOLEAN: // booleans are rendered as strings (i.e. "true"/"false")
        return new StringThTextAttribute(dataSrcPath, fieldContext.stringFormat!, fieldContext.defaultEmptyValue);
      case FieldType.DATE:
        return new DateThTextAttribute(dataSrcPath, fieldContext.dateFormatOptions!, fieldContext.defaultEmptyValue);
      case FieldType.INTEGER:
        return new IntegerThTextAttribute(dataSrcPath, fieldContext.integerFormat!, fieldContext.defaultEmptyValue);
      case FieldType.DOUBLE:
        return new DecimalThTextAttribute(dataSrcPath, fieldContext.decimalFormat!, fieldContext.defaultEmptyValue);
    }

    throw new Error(`Failed to make ThTextAttribute. Unknown field type '${fieldContext.field.type}'`);
  }

  private static escapeConditions(conditions: Condition[]): Condition[] {
    return conditions.map(
      (condition) =>
        new Condition(
          ThTextAttributeFactory.escapeFieldPath(condition.fieldPath),
          condition.fieldType,
          condition.operator,
          condition.value
        )
    );
  }

  private static escapeFieldPath(fieldPath: string): string {
    return fieldPath
      .split('.')
      .map((segment) => segment.replace(/__/g, '\\_\\_'))
      .join('.');
  }
}
